import { NgClass } from "@angular/common"
import { ApplicationRef, Component, effect, ElementRef, EmbeddedViewRef, EventEmitter, forwardRef, Injector, Input, OnChanges, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild } from "@angular/core"
import { FormControl, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from "@angular/forms"
import { SelectCode, SelectCodeValue, SelectCurrency, SelectImage, SelectPhone } from "@corporate-tailwind/model"
import { debounceTime, distinctUntilChanged, startWith } from "rxjs"
import { CorpControlCommonComponent } from "../common/corp-control-common/corp-control-common.component"
import { CorpControlTitleComponent } from "../common/corp-control-title/corp-control-title.component"

@Component({
    selector: 'corp-dropdown',
    standalone: true,
    templateUrl: './corp-dropdown.component.html',
    imports: [NgClass, CorpControlTitleComponent, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CorpDropdownComponent),
            multi: true,
        },
    ],
})
export class CorpDropdownComponent extends CorpControlCommonComponent implements OnInit, OnDestroy, OnChanges {
    @Input() options: SelectCode[] | SelectCodeValue[] | SelectImage[] | SelectCurrency[] | SelectPhone[] = [];
    @Input() selected: SelectCode | SelectCodeValue | SelectImage | SelectCurrency | null | SelectPhone = null;
    @Output() selectedChange = new EventEmitter<SelectCode>();

    @ViewChild('overlayTpl', { static: true })
    overlayTpl!: TemplateRef<any>

    @ViewChild('trigger', { static: true })
    trigger!: ElementRef<HTMLElement>

    private overlayEl?: HTMLElement
    private overlayView?: EmbeddedViewRef<any>
    isOpen = false;

    filteredOptions: any[] = []; // liste affichée
    searchControl = new FormControl('');
    private readonly _searchSignal = signal('');
hoveredOption: number | string | null = null;

    constructor(
        private host: ElementRef<HTMLElement>,
        private appRef: ApplicationRef,
        private injector: Injector,
    ) {
        super()

        // initialisation
        this.filteredOptions = this.options;
        this.searchControl.valueChanges
            .pipe(
                startWith(''),
                debounceTime(100),
                distinctUntilChanged()
            ).subscribe(x => {
                this._searchSignal.set(this.searchControl.value || '')
            });


        effect(() => {
            // const search = this._searchSignal();    // ⚡ dépendance
            //this._corpTranslationService.lang$()   // ⚡ dépendance

            this.updateFilteredOptions()

            // // const translatedOptions = this._corpTranslationService.translate(this.options);

            // if(this.options){
            //     this.filteredOptions = this.options.filter(opt =>
            //         opt.label?.toLowerCase().includes(search.toLowerCase()) ||
            //         opt.code?.toLowerCase().includes(search.toLowerCase())
            //     );
            // }
        })
    }

    // ngAfterViewInit() {
    //     if (this.disabled || this.corpReadonly) return
    //     this.trigger.nativeElement.addEventListener('click', () => this.toggle())
    // }

    ngOnInit(): void {
        // relie ce composant au FormControl parent
        const ngControl = this.injector.get(NgControl, null)
        this.setNgControl(ngControl)
    }

    ngOnChanges() {
        this.updateFilteredOptions()
    }

    private updateFilteredOptions() {
        const search = this._searchSignal()

        if (this.options) {
            this.filteredOptions = this.options.filter(opt =>
                opt.label?.toLowerCase().includes(search.toLowerCase()) ||
                opt.code?.toLowerCase().includes(search.toLowerCase())
            )
        }
    }

    override writeValue(value: any): void {
        super.writeValue(value)
        this.selected = value
    }

    override setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled)
    }

    toggle(event?: MouseEvent) {
        if (this.disabled || this.corpReadonly) return;

        event?.stopPropagation();

        this.isOpen ? this.close() : this.open();
    }

    // toggle() {
    //     if (this.disabled || this.corpReadonly) return;

    //     if (this.isOpen) {
    //         this.close();
    //     } else {
    //         this.open();
    //     }
    // }

    open() {
        if (this.isOpen) return

        this.isOpen = true

        //pour repositionnement list
        window.addEventListener('scroll', this.onScroll, true);
        window.addEventListener('resize', this.onScroll);

        // récupère la position et largeur du trigger
        const rect = this.trigger.nativeElement.getBoundingClientRect()

        // crée la vue du template
        this.overlayView = this.overlayTpl.createEmbeddedView({ options: this.options })
        this.appRef.attachView(this.overlayView)

        // récupère l’élément HTML réel rendu
        const el = this.overlayView.rootNodes[0] as HTMLElement

        // position fixe pour overlay
        el.style.position = 'fixed'
        el.style.zIndex = '9999'
        el.style.left = `${rect.left}px`
        el.style.top = `${rect.bottom + 4}px`
        //largeur selection independante
        el.style.minWidth = `${rect.width}px`
        // el.style.width = `${rect.width}px`

        // force flex-column pour que la liste scroll correctement
        el.style.display = 'flex'
        el.style.flexDirection = 'column'

        // ajoute au DOM
        document.body.appendChild(el)
        this.overlayEl = el

        // ⚡ gère la hauteur max de la liste
        const list = el.querySelector('.dropdown-list') as HTMLElement
        if (list) {
            const triggerHeight = this.trigger.nativeElement.offsetHeight
            list.style.maxHeight = `${triggerHeight * 4}px` // 4 items max
            list.style.overflowY = 'auto'
        }

        // fermeture au clic extérieur
        setTimeout(() => document.addEventListener('click', this.closeOutside));
    }

    close() {
        if (!this.isOpen) return;

        this.isOpen = false;

        //pour repositionnement list
        window.removeEventListener('scroll', this.onScroll, true);
        window.removeEventListener('resize', this.onScroll);

        if (this.overlayView) {
            this.appRef.detachView(this.overlayView)
            this.overlayView.destroy()
            this.overlayView = undefined
        };

        if (this.overlayEl) {
            this.overlayEl.remove()
            this.overlayEl = undefined
        };

        // retirer listener global
        document.removeEventListener('click', this.closeOutside, true)
    }

    // fermeture via clic extérieur
    private closeOutside = (event: MouseEvent) => {
        if (!this.overlayEl) return;

        const target = event.target as Node;
        const clickedInsideOverlay = this.overlayEl.contains(target);
        const clickedTrigger = this.trigger.nativeElement.contains(target);

        if (!clickedInsideOverlay && !clickedTrigger) {
            this.close();
        }
        // if (!this.overlayEl) return
        // if (!this.overlayEl.contains(event.target as Node)) {
        //     this.close()
        // }
    };

    select(value: SelectCode | SelectCurrency) {
        if ('availability' in value && !value?.availability?.isAvailable) {
            return;
        }
        this.selected = value;
        this.selectedChange.emit(value)
        this.value = value
        this.onChange(value) // notifie Angular FormControl
        this.close()
    }

    isEqual(a: SelectCode | null, b: SelectCode | null): boolean {
        if (!a || !b) return false
        return a.id === b.id || (!!a.code && !!b.code && a.code === b.code)
    }

    ngOnDestroy(): void {
        this.close()
    }

    private onScroll = () => {
        this.updateOverlayPosition()
    };

    private updateOverlayPosition() {
        if (!this.overlayEl) return;

        const rect = this.trigger.nativeElement.getBoundingClientRect();

        this.overlayEl.style.left = `${rect.left}px`;
        this.overlayEl.style.top = `${rect.bottom + 4}px`;

        //largeur selection independante
        // this.overlayEl.style.width = `${rect.width}px`;
        this.overlayEl.style.minWidth = `${rect.width}px`;
    }
}
